import type { RouteSheetPrintData, RouteSheetStageRow } from '@/services/workOrder.service';

export type RouteSheetMaterialEntry = {
  drawing_line_no: string | null;
  client_material_specification: string | null;
  material_grade: string | null;
  heat_no: string | null;
  verified?: boolean;
  not_verified?: boolean;
  remarks?: string | null;
};

/** Material request row scoped to an operator page (by WO line). */
export type RouteSheetMrMaterialEntry = {
  material: string | null;
  requested_qty: string | null;
  issued_qty: string | null;
  heat_number: string | null;
  request_number: string | null;
  boq_line_label: string | null;
  remarks: string | null;
};

export type RouteSheetOperatorPage = {
  groupLabel: string;
  /** All operations for this BOQ item (one print page). */
  operations: RouteSheetStageRow[];
  /** Material utilization rows for this page (line-scoped + WO-level). */
  material_entries: RouteSheetMaterialEntry[];
  /** MR materials for this BOQ page (by work_order_line_item_id). */
  mr_materials: RouteSheetMrMaterialEntry[];
  work_order_line_item_id?: string | null;
  item_code?: string | null;
  revision?: string | null;
  boq_line_no?: string | null;
  item_description?: string | null;
  drawing_no?: string | null;
  quantity?: number | null;
  uom?: string | null;
};

/**
 * Break inline numbered / bullet markers onto new lines for print readability.
 * Matches `1)`, `2.`, `(3)`, and bullet chars; skips qty notes like `(QTY of pin 2).`
 */
export function formatItemDescriptionListBreaks(text: string): string {
  if (!text) return text;
  let s = String(text).replace(/\r\n/g, '\n');

  // Numbered list: "1)" / "2." followed by item text (letter or open paren)
  s = s.replace(/\s+(\d+[\)\.])\s*(?=[A-Za-z(])/g, '\n$1 ');
  // Parenthesized numbers: "(1) text"
  s = s.replace(/\s+(\(\d+\))\s*(?=[A-Za-z])/g, '\n$1 ');
  // Common bullets
  s = s.replace(/\s+([•●○▪])\s+/g, '\n$1 ');
  s = s.replace(/\s+([-–—])\s+(?=[A-Za-z])/g, '\n$1 ');

  return s
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .filter((line, i, arr) => line.length > 0 || (i > 0 && i < arr.length - 1))
    .join('\n')
    .trim();
}

function mapUtilizationRow(m: {
  drawing_line_no?: string | null;
  client_material_specification?: string | null;
  material_grade?: string | null;
  head_no?: string | null;
  heat_no?: string | null;
  verified?: boolean;
  not_verified?: boolean;
  remarks?: string | null;
}): RouteSheetMaterialEntry {
  return {
    drawing_line_no: m.drawing_line_no ?? null,
    client_material_specification: m.client_material_specification ?? null,
    material_grade: m.material_grade ?? null,
    heat_no: m.heat_no ?? m.head_no ?? null,
    verified: Boolean(m.verified),
    not_verified: Boolean(m.not_verified),
    remarks: m.remarks ?? null,
  };
}

function mapMrMaterialRow(m: RouteSheetPrintData['materials'][number]): RouteSheetMrMaterialEntry {
  return {
    material: m.material ?? null,
    requested_qty: m.requested_qty ?? m.qty ?? null,
    issued_qty: m.issued_qty ?? null,
    heat_number: m.heat_number ?? null,
    request_number: m.request_number ?? null,
    boq_line_label: m.boq_line_label ?? null,
    remarks: m.remarks ?? null,
  };
}

/**
 * One operator handoff page per BOQ item (not per operation).
 * Prefers `boq_lines`; falls back to a single WO-level page from fabrication units.
 * Material utilization: only rows linked to that BOQ / WO line item (no WO-level broadcast).
 * MR materials: scoped by work_order_line_item_id; unassigned MR lines appear on the first page only.
 */
export function flattenRouteSheetOperatorPages(data: RouteSheetPrintData): RouteSheetOperatorPage[] {
  const allUtil = data.material_utilization || [];

  const allMr = data.materials || [];
  const unscopedMr = allMr.filter((m) => !m.work_order_line_item_id).map(mapMrMaterialRow);

  const boqLines = data.boq_lines || [];
  if (boqLines.length > 0) {
    return boqLines.map((line, idx) => {
      const lineId = line.work_order_line_item_id ? String(line.work_order_line_item_id).trim() : '';
      // Prefer full util payload (verified/remarks); fall back to boq material_entries
      const fromUtil = allUtil
        .filter((m) => m.work_order_line_item_id && String(m.work_order_line_item_id).trim() === lineId)
        .map(mapUtilizationRow);
      const lineScoped =
        fromUtil.length > 0
          ? fromUtil
          : (line.material_entries || []).map(mapUtilizationRow);

      const lineMr = allMr
        .filter((m) => m.work_order_line_item_id && String(m.work_order_line_item_id).trim() === lineId)
        .map(mapMrMaterialRow);
      // Surface unassigned MR lines on the first page so they are not hidden.
      const mr_materials = idx === 0 ? [...lineMr, ...unscopedMr] : lineMr;

      return {
        groupLabel: `Line ${line.line_number}: ${line.item_description}`,
        operations: [...(line.operations || [])],
        material_entries: lineScoped,
        mr_materials,
        work_order_line_item_id: line.work_order_line_item_id,
        item_code: line.item_code ?? data.header.item_code ?? null,
        revision: line.revision ?? data.header.item_revision ?? null,
        boq_line_no: line.boq_line_no ?? String(line.line_number),
        item_description: line.item_description,
        drawing_no: line.drawing_no ?? data.header.drawing_no ?? null,
        quantity: line.quantity,
        uom: line.uom,
      };
    });
  }

  // Unit-wise WO with no BOQ line breakdown — one page for the whole WO
  const operations: RouteSheetStageRow[] = [];
  for (const unit of data.fabrication_units || []) {
    for (const op of unit.operations || []) {
      operations.push(op);
    }
  }
  if (!operations.length) return [];

  const material_entries: RouteSheetMaterialEntry[] = allUtil.map(mapUtilizationRow);
  const mr_materials: RouteSheetMrMaterialEntry[] = allMr.map(mapMrMaterialRow);

  return [
    {
      groupLabel: data.header.item_code
        ? `Item ${data.header.item_code}`
        : data.header.work_order_number || 'Work order',
      operations,
      material_entries,
      mr_materials,
      item_code: data.header.item_code ?? null,
      revision: data.header.item_revision ?? null,
      boq_line_no: data.header.boq_line_no ?? null,
      item_description: data.header.item_name ?? null,
      drawing_no: data.header.drawing_no ?? null,
      quantity: data.header.quantity,
      uom: data.header.uom,
    },
  ];
}
